import type { Router } from 'vue-router';
import {
  ApiError,
  createApiClient,
  type OrgEnterResponse,
  type OrgSelectionRequiredResponse,
  type OrgSelectionEntry,
  type OrgSelectResponse,
} from '@neon-one/auctions-types';

import { bareAdminUrl } from '@/shell/org';
import { env } from '@/shell/env';
import { useAdminSession } from '@/shell/stores/adminSession';
import { adoptJwt } from '@/shell/auth/loadAdminUser';
import { publishToast } from '@/shell/toast/bus';

/** Backend bind endpoint after multi-org rebind (wire: OrgAdoptRequest). */
export const ORG_ADOPT_API_PATH = '/admin/org-adopt' as const;

/** Backend org-select endpoint after in-app picker choice (wire: OrgSelectRequest). */
export const ORG_SELECT_API_PATH = '/admin/org-select' as const;

/** Backend endpoint that re-opens the picker from an org-bound session. */
export const ORG_SWITCH_API_PATH = '/admin/org-switch' as const;

/** Backend endpoint that binds the session to a linked org's subdomain. */
export const ORG_ENTER_API_PATH = '/admin/org-enter' as const;

/** Shown when a followed link points at an org the user cannot enter. */
export const ORG_NO_ACCESS_PATH = '/no-access' as const;

/** Backend SSO callback path. */
export const AUTH_CALLBACK_API_PATH = '/admin/auth/callback' as const;

/** In-app multi-org picker route (no admin session yet). */
export const ORG_PICKER_PATH = '/select-organization' as const;

/** Portal/query param name for the chosen SSO organization id (legacy round-trip). */
export const SSO_ORG_ID_QUERY = 'sso_org_id' as const;

/** Default in-app landing after a successful bind. */
export const DEFAULT_POST_AUTH_PATH = '/auctions' as const;

/**
 * sessionStorage key for the in-flight multi-org selection payload
 * (`selectionToken` + `organizations` + post-auth next path). Held the same
 * way a session JWT would be held client-side — never localStorage.
 */
export const ORG_SELECTION_STORAGE_KEY = 'neon-auctions:org-selection' as const;

/** Client-held state between SSO callback `selectionRequired` and org-select. */
export interface OrgSelectionState {
  selectionToken: string;
  organizations: OrgSelectionEntry[];
  nextPath: string;
}

/**
 * Parse `sso_org_id` / `ssoOrgId` from a route query value.
 * Mirrors backend `parseSelectedSsoOrgId` — only positive integers bind.
 */
export function parseSsoOrgIdQuery(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value === 'number') {
    if (!Number.isInteger(value) || value <= 0) return null;
    return value;
  }
  if (typeof value !== 'string') return null;
  const n = Number.parseInt(value, 10);
  if (!Number.isFinite(n) || n <= 0 || String(n) !== value.trim()) return null;
  return n;
}

function isOrgSelectionEntry(value: unknown): value is OrgSelectionEntry {
  if (!value || typeof value !== 'object') return false;
  const entry = value as Record<string, unknown>;
  if (typeof entry.ssoOrgId !== 'number' || !Number.isInteger(entry.ssoOrgId) || entry.ssoOrgId <= 0) {
    return false;
  }
  if (typeof entry.name !== 'string' || entry.name.length < 1) return false;
  if (entry.orgSlug != null && typeof entry.orgSlug !== 'string') return false;
  return true;
}

function parseOrgSelectionState(raw: string | null): OrgSelectionState | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const obj = parsed as Record<string, unknown>;
    if (typeof obj.selectionToken !== 'string' || obj.selectionToken.length < 1) return null;
    if (!Array.isArray(obj.organizations) || obj.organizations.length < 1) return null;
    if (!obj.organizations.every(isOrgSelectionEntry)) return null;
    const nextPath = safePostAuthPath(
      typeof obj.nextPath === 'string' ? obj.nextPath : DEFAULT_POST_AUTH_PATH,
    );
    return {
      selectionToken: obj.selectionToken,
      organizations: obj.organizations,
      nextPath,
    };
  } catch {
    return null;
  }
}

/** Persist selection payload for the in-app picker (sessionStorage only). */
export function stashOrgSelection(state: OrgSelectionState): void {
  if (!state.selectionToken || state.organizations.length < 1) return;
  const payload: OrgSelectionState = {
    selectionToken: state.selectionToken,
    organizations: state.organizations,
    nextPath: safePostAuthPath(state.nextPath),
  };
  try {
    sessionStorage.setItem(ORG_SELECTION_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage may be unavailable (private mode quotas) — non-fatal.
  }
}

/** Read selection state without clearing (picker re-renders / recovery). */
export function loadOrgSelection(): OrgSelectionState | null {
  try {
    return parseOrgSelectionState(sessionStorage.getItem(ORG_SELECTION_STORAGE_KEY));
  } catch {
    return null;
  }
}

/** True when a usable selection payload is present. */
export function hasOrgSelectionState(): boolean {
  return loadOrgSelection() != null;
}

/** Drop selection state (after successful bind, expired token, or restart). */
export function clearOrgSelection(): void {
  try {
    sessionStorage.removeItem(ORG_SELECTION_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Read + clear selection state (one-shot consume after successful select). */
export function consumeOrgSelection(): OrgSelectionState | null {
  const state = loadOrgSelection();
  clearOrgSelection();
  return state;
}

/**
 * Recovery target after org-adopt / meFromClaims mismatch.
 * Prefer the in-app picker when selection state is still held; otherwise
 * bare-admin `/login` so SSO restarts. Never synthesizes a Portal URL.
 */
export function orgSelectionRecoveryUrl(): string {
  if (hasOrgSelectionState()) {
    return bareAdminUrl(ORG_PICKER_PATH);
  }
  return bareAdminUrl('/login');
}

/**
 * Open the org picker from an org-bound session. POST /admin/org-switch
 * returns a fresh selection token, so the picker and org-select run exactly
 * as they do after SSO. The current session stays in place until org-select
 * replaces it, so browser Back returns to the current org.
 *
 * Uses a dedicated client: the shell httpClient would cover a refused switch
 * with its generic "Permission denied" toast. A 401 means the session itself
 * is gone, so sign-in restarts from the bare host, which also lands a
 * multi-org user on the picker.
 */
export async function switchOrganization(router: Router): Promise<void> {
  const session = useAdminSession();
  const client = createApiClient({
    baseUrl: env.apiBaseUrl,
    getJwt: () => session.getJwt(),
  });
  try {
    const resp = await client.post<OrgSelectionRequiredResponse>(ORG_SWITCH_API_PATH, {});
    clearOrgSelection();
    stashOrgSelection({
      selectionToken: resp.data.selectionToken,
      organizations: resp.data.organizations,
      nextPath: DEFAULT_POST_AUTH_PATH,
    });
    await router.push(ORG_PICKER_PATH);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      clearOrgSelection();
      session.clear();
      window.location.assign(bareAdminUrl('/login'));
      return;
    }
    publishToast(
      e instanceof ApiError && e.code === 'ORG_SWITCH_UNAVAILABLE'
        ? {
            severity: 'warn',
            summary: 'No other organizations',
            detail: 'Your account has access to only one organization.',
          }
        : {
            severity: 'danger',
            summary: "Couldn't switch organizations",
            detail: 'Please try again.',
          },
    );
  }
}

/**
 * Constrain an in-app next-path before interpolating it into a URL.
 * Protocol-relative and off-path values fall back to the default landing.
 */
export function safePostAuthPath(raw: string | null | undefined): string {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return DEFAULT_POST_AUTH_PATH;
  return raw;
}

/**
 * POST /admin/org-select with the selection token as Bearer.
 *
 * Uses a dedicated client (not the shell httpClient) so a 401 on an expired
 * selection token does not trip the global session-expired redirect — the
 * picker owns 401 → restart sign-in and 403 → stay-with-error itself.
 */
export async function postOrgSelect(ssoOrgId: number): Promise<OrgSelectResponse> {
  const state = loadOrgSelection();
  if (!state?.selectionToken) {
    throw new ApiError(401, {
      code: 'UNAUTHORIZED',
      message: 'Organization selection has expired. Please sign in again.',
    } as never);
  }
  const client = createApiClient({
    baseUrl: env.apiBaseUrl,
    getJwt: () => state.selectionToken,
  });
  const resp = await client.post<OrgSelectResponse>(ORG_SELECT_API_PATH, { ssoOrgId });
  return resp.data;
}

export type EnterOrgOutcome = 'entered' | 'denied' | 'expired' | 'error';

/**
 * Bind the session to the org whose subdomain the user is on, for a followed
 * link that points at an org other than the session's. On `entered` the new
 * session cookie is written and the caller reloads the page. On `expired` the
 * session is already cleared.
 *
 * Dedicated client for the same reason as switchOrganization: the shell
 * httpClient would cover a denial with its generic toast.
 */
export async function enterOrg(orgSlug: string): Promise<EnterOrgOutcome> {
  const session = useAdminSession();
  const client = createApiClient({
    baseUrl: env.apiBaseUrl,
    getJwt: () => session.getJwt(),
  });
  try {
    const resp = await client.post<OrgEnterResponse>(ORG_ENTER_API_PATH, { orgSlug });
    // A bind to any other org would send the guard round the redirect again.
    if (resp.data.orgSlug !== orgSlug) return 'error';
    // The bound slug also travels beside `admin`; keep it on the session so
    // the guard can recognise this org's subdomain on later navigations.
    const admin = { ...resp.data.admin, orgSlug: resp.data.admin.orgSlug ?? resp.data.orgSlug };
    return adoptJwt(resp.data.jwt, admin, { currentOrgSlug: orgSlug }) ? 'entered' : 'error';
  } catch (e) {
    // Revoked in the linked org only: the current session is still good.
    if (e instanceof ApiError && e.code === 'ACCOUNT_REVOKED') return 'denied';
    if (e instanceof ApiError && e.status === 401) {
      session.clear();
      return 'expired';
    }
    if (e instanceof ApiError && e.status === 403) return 'denied';
    return 'error';
  }
}
